import { useState, useCallback, useEffect } from 'react';
import styled from 'styled-components';
import { Logo } from './Logo';
import { Select } from './Select';
import { FilterInput } from './FilterInput';
import { Button } from './Button';
import { useData } from '../providers';

const API_URL = 'https://rickandmortyapi.com/api/character/';

const STATUS_OPTIONS = ['Alive', 'Dead', 'Unknown'];
const GENDER_OPTIONS = ['Female', 'Male', 'Genderless', 'Unknown'];
const SPECIES_OPTIONS = [
  'Human',
  'Alien',
  'Humanoid',
  'Animal',
  'Robot',
  'Mythological Creature',
  'Disease',
  'Cronenberg',
  'Poopybutthole',
  'Planet',
  'Unknown'
];

const defaultFilters = {
  status: null,
  gender: null,
  species: null,
  name: '',
  type: ''
};

function buildURL(filters) {
  const url = new URL(API_URL);

  if (filters.status)
    url.searchParams.set('status', filters.status.toLowerCase());
  if (filters.gender)
    url.searchParams.set('gender', filters.gender.toLowerCase());
  if (filters.species)
    url.searchParams.set('species', filters.species.toLowerCase());
  if (filters.name.trim()) url.searchParams.set('name', filters.name.trim());
  if (filters.type.trim()) url.searchParams.set('type', filters.type.trim());

  return url;
}

function readFiltersFromURL() {
  const params = new URLSearchParams(window.location.search);
  const capitalize = (s) => s.charAt(0).toUpperCase() + s.slice(1);

  return {
    status: params.get('status') ? capitalize(params.get('status')) : null,
    gender: params.get('gender') ? capitalize(params.get('gender')) : null,
    species: params.get('species') ? capitalize(params.get('species')) : null,
    name: params.get('name') || '',
    type: params.get('type') || ''
  };
}

export function Header() {
  const { setApiURL, setActivePage } = useData();
  const [filters, setFilters] = useState(readFiltersFromURL);

  useEffect(() => {
    const urlFilters = readFiltersFromURL();
    const hasFilters = Object.values(urlFilters).some(
      (v) => v !== null && v !== ''
    );

    if (hasFilters) {
      setApiURL(buildURL(urlFilters).toString());
    }
  }, [setApiURL]);

  const handleApply = useCallback(() => {
    const url = buildURL(filters);
    window.history.replaceState({}, '', `?${url.searchParams.toString()}`);
    setActivePage(0);
    setApiURL(url.toString());
  }, [filters, setApiURL, setActivePage]);

  const handleReset = useCallback(() => {
    setFilters(defaultFilters);
    window.history.replaceState({}, '', window.location.pathname);
    setActivePage(0);
    setApiURL(API_URL);
  }, [setApiURL, setActivePage]);

  const handleStatusChange = useCallback(
    (val) => setFilters((prev) => ({ ...prev, status: val })),
    []
  );
  const handleGenderChange = useCallback(
    (val) => setFilters((prev) => ({ ...prev, gender: val })),
    []
  );
  const handleSpeciesChange = useCallback(
    (val) => setFilters((prev) => ({ ...prev, species: val })),
    []
  );
  const handleNameChange = useCallback(
    (val) => setFilters((prev) => ({ ...prev, name: val })),
    []
  );
  const handleTypeChange = useCallback(
    (val) => setFilters((prev) => ({ ...prev, type: val })),
    []
  );

  return (
    <HeaderContainer>
      <Logo />
      <FiltersContainer>
        <FiltersTopRow>
          <Select
            placeholder="Status"
            options={STATUS_OPTIONS}
            value={filters.status}
            onChange={handleStatusChange}
          />
          <Select
            placeholder="Gender"
            options={GENDER_OPTIONS}
            value={filters.gender}
            onChange={handleGenderChange}
          />
          <Select
            placeholder="Species"
            options={SPECIES_OPTIONS}
            value={filters.species}
            onChange={handleSpeciesChange}
          />
        </FiltersTopRow>
        <FiltersBottomRow>
          <InputsGroup>
            <FilterInput
              placeholder="Name"
              value={filters.name}
              onChange={handleNameChange}
            />
            <FilterInput
              placeholder="Type"
              value={filters.type}
              onChange={handleTypeChange}
            />
          </InputsGroup>
          <ButtonsGroup>
            <Button onClick={handleApply}>Apply</Button>
            <Button onClick={handleReset} variant="reset">
              Reset
            </Button>
          </ButtonsGroup>
        </FiltersBottomRow>
      </FiltersContainer>
    </HeaderContainer>
  );
}

const HeaderContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-around;
  border-radius: 12px;
  padding: 20px 30px;
  gap: 16px;

  @media (max-width: 950px) {
    flex-direction: column;
    align-items: center;
  }
`;

const FiltersTopRow = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 180px);
  gap: 10px;

  @media (max-width: 950px) {
    grid-template-columns: repeat(3, 150px);
    gap: 15px;
  }

  @media (max-width: 530px) {
    grid-template-columns: 1fr;
    width: 240px;
  }
`;

const FiltersBottomRow = styled.div`
  display: flex;
  gap: 15px;

  @media (max-width: 530px) {
    flex-direction: column;
    width: 240px;
  }
`;

const InputsGroup = styled.div`
  display: flex;
  gap: 10px;

  & input {
    width: 180px;
    height: 40px;
  }

  @media (max-width: 950px) {
    gap: 15px;

    & input {
      width: 150px;
    }
  }

  @media (max-width: 530px) {
    flex-direction: column;

    & input {
      width: 240px;
    }
  }
`;

const ButtonsGroup = styled.div`
  display: flex;
  gap: 10px;

  & button {
    width: 85px;
    height: 40px;
  }

  @media (max-width: 950px) {
    gap: 10px;

    & button {
      width: 70px;
    }
  }

  @media (max-width: 530px) {
    flex-direction: column;
    gap: 15px;

    & button {
      width: 240px;
    }
  }
`;

const FiltersContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;

  @media (max-width: 950px) {
    align-items: center;
  }
`;
